import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Form, Grid, Header, Message } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

/**
 * 开机自启动设置（仅 Windows 会显示这个 tab，见 pages/Setting/index.js）。
 *
 * 语义上分两种情形，界面必须说清楚，否则用户会以为开了开关就等于无人值守也能跑：
 *  - mode=logon：给当前用户写注册表 Run 项，登录 Windows 后自动拉起托盘，免管理员权限；
 *  - mode=service：当前进程本身就是 Windows 服务，自启由服务提供（开机即启、无需登录），
 *    开关只读。
 * 其他平台后端返回 supported=false，这个 tab 根本不会出现。
 */
const AutoStartSetting = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/autostart');
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setState(data);
    } catch (error) {
      // HTTP 层错误已由 API 拦截器提示，这里不重复弹
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().then();
  }, []);

  const toggle = async () => {
    if (!state || state.mode === 'service' || saving) return;
    const next = !state.enabled;
    setSaving(true);
    try {
      const res = await API.post('/api/autostart', { enabled: next });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setState(data);
      showSuccess(
        next
          ? t('setting.autostart.toast_enabled')
          : t('setting.autostart.toast_disabled')
      );
    } catch (error) {
      // 同上，拦截器已经提示过
    } finally {
      setSaving(false);
    }
  };

  const modeText =
    state?.mode === 'service'
      ? t('setting.autostart.mode_service')
      : t('setting.autostart.mode_logon');
  // 换过位置留下的旧自启项：能读到但指向的不是当前这份 exe，提示用户重开一次开关
  const staleRegistered =
    state?.registered && state?.command && state.registered !== state.command;

  return (
    <div className='settings-setting-view settings-autostart'>
      <Grid columns={1}>
        <Grid.Column>
          <Form loading={loading} className='settings-form-stack'>
            <Header
              as='h3'
              style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}
            >
              {t('setting.autostart.title')}
            </Header>

            {state && !state.supported && (
              <Message info size='small' content={t('setting.autostart.unsupported')} />
            )}

            {state && state.supported && (
              <>
                <Form.Group inline>
                  <label>{t('setting.autostart.switch_label')}</label>
                  <Form.Checkbox
                    toggle
                    checked={state.enabled}
                    disabled={state.mode === 'service' || saving}
                    onChange={toggle}
                  />
                  <span style={{ marginLeft: 12, color: '#71717a', fontSize: 13 }}>
                    {state.detail}
                  </span>
                </Form.Group>

                <div style={{ marginTop: 8, fontSize: 13, color: '#52525b', lineHeight: 1.9 }}>
                  <div>
                    <strong>{t('setting.autostart.mode_label')}：</strong>
                    {modeText}
                  </div>
                  <div>
                    <strong>{t('setting.autostart.command_label')}：</strong>
                    <code style={{ marginLeft: 4 }}>{state.command || '-'}</code>
                  </div>
                  {staleRegistered && (
                    <div style={{ color: '#b45309' }}>
                      {t('setting.autostart.stale_registered', { registered: state.registered })}
                    </div>
                  )}
                </div>

                <Divider />
                <p style={{ color: 'gray', fontSize: 12, lineHeight: 1.9 }}>
                  {t('setting.autostart.service_hint')}
                </p>
              </>
            )}
          </Form>
        </Grid.Column>
      </Grid>
    </div>
  );
};

export default AutoStartSetting;
